import React, { useEffect, useRef, useState } from 'react'
import { 
  Paper, 
  Box, 
  Typography, 
  Divider, 
  TextField, 
  Button, 
  CircularProgress, 
  IconButton,
  Avatar,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Alert,
  Skeleton,
  Card
} from '@mui/material'
import { format, parseISO, isToday, isYesterday } from 'date-fns'
import { sv } from 'date-fns/locale'
import { useConversations } from '../contexts/ConversationsContext'
import { supabase } from '../lib/supabase'
import SendIcon from '@mui/icons-material/Send'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import api from '../services/api'

export default function MessageThread({ conversation, onBack }) {
  const messagesEndRef = useRef(null)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [messageText, setMessageText] = useState('')
  const [sendingMessage, setSendingMessage] = useState(false)
  const [sendError, setSendError] = useState(null)
  const [currentState, setCurrentState] = useState(null)
  const { refreshConversation } = useConversations()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isSmall = useMediaQuery(theme.breakpoints.down('md'))

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Check if manual response is allowed based on current state
  const canSendManualResponse = currentState === 'manual_conversation'

  // Load messages directly from Supabase
  const loadMessages = async () => {
    if (!conversation) return
    
    setLoading(true)
    
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversation.id)
        .order('created_at', { ascending: true })
      
      if (error) {
        console.error('Error loading messages:', error)
        return
      }
      
      setMessages(data || [])
    } catch (err) {
      console.error('Error in loadMessages:', err)
    } finally {
      setLoading(false)
    }
  }

  // Send a manual response
  const sendManualResponse = async () => {
    if (!messageText.trim() || !conversation?.id) return
    
    setSendingMessage(true)
    setSendError(null)
    
    try {
      // Use the api utility instead of direct fetch
      await api.post('/api/feedback/manual-response', {
        conversation_id: conversation.id,
        message: messageText.trim()
      });
      
      // Clear the input field after successful send
      setMessageText('')
      
      // Refresh the conversation to get the updated state
      await refreshConversation(conversation.id)
    } catch (error) {
      console.error('Error sending manual response:', error)
      setSendError(error.response?.data?.detail || 'Kunde inte skicka meddelande')
    } finally {
      setSendingMessage(false)
    }
  }

  // Handle Enter key press in the text field
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendManualResponse()
    }
  }

  // Update current state when conversation changes
  useEffect(() => {
    if (conversation) {
      setCurrentState(conversation.state)
    } else {
      setCurrentState(null)
    }
  }, [conversation])

  // Load messages when conversation changes
  useEffect(() => {
    if (conversation) {
      loadMessages()
    } else {
      setMessages([])
    }
  }, [conversation?.id])

  // Set up real-time subscription for this specific conversation
  useEffect(() => {
    if (!conversation) return
    
    // Subscribe to messages
    const messagesChannel = supabase
      .channel(`messages-for-${conversation.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversation.id}`
        },
        (payload) => {
          setMessages(current => [...current, payload.new])
        }
      )
      .subscribe()
    
    // Subscribe to conversation state changes
    const conversationChannel = supabase
      .channel(`conversation-state-${conversation.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
          filter: `id=eq.${conversation.id}`
        },
        (payload) => {
          // Update the current state when it changes in the database
          setCurrentState(payload.new.state)
        }
      )
      .subscribe()
    
    return () => {
      messagesChannel.unsubscribe()
      conversationChannel.unsubscribe()
    }
  }, [conversation?.id])

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom()
  }, [messages.length])

  if (!conversation) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column',
          borderRadius: { xs: 0, sm: 2 },
          p: 3
        }}
      >
        <SmartToyOutlinedIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Välj en konversation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
          Välj en konversation från listan för att visa meddelanden
        </Typography>
      </Paper>
    )
  }

  const formatDate = (dateString) => {
    try {
      if (!dateString) return ''
      const date = parseISO(dateString)
      
      if (isToday(date)) {
        return `Idag, ${format(date, 'p', { locale: sv })}`
      } else if (isYesterday(date)) {
        return `Igår, ${format(date, 'p', { locale: sv })}`
      } else {
        return format(date, 'PPp', { locale: sv })
      }
    } catch (error) {
      console.error('Error formatting date:', dateString, error)
      return 'Ogiltigt datum'
    }
  }

  // Sort messages by date
  const sortedMessages = [...messages].sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  )

  const groupMessagesByDate = () => {
    const groups = {}
    
    sortedMessages.forEach(message => {
      try {
        const date = parseISO(message.created_at)
        const dateKey = format(date, 'yyyy-MM-dd')
        
        if (!groups[dateKey]) {
          groups[dateKey] = []
        }
        
        groups[dateKey].push(message)
      } catch (error) {
        console.error('Error grouping message:', error)
      }
    })
    
    return groups
  }
  
  const messageGroups = groupMessagesByDate()

  return (
    <Paper 
      elevation={0}
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: { xs: 0, sm: 2 },
        overflow: 'hidden'
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper
        }}
      >
        {isMobile && (
          <IconButton 
            onClick={onBack} 
            sx={{ mr: 1 }}
            edge="start"
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            sx={{ 
              mr: 2, 
              bgcolor: getStateColor(currentState || conversation.state).bgColor,
              color: getStateColor(currentState || conversation.state).textColor
            }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight="600">
              {conversation.customer_name || conversation.phone_number}
            </Typography>
            {conversation.customer_name && (
              <Typography variant="body2" color="text.secondary">
                {conversation.phone_number}
              </Typography>
            )}
            <Typography 
              variant="caption" 
              sx={{ 
                backgroundColor: getStateColor(currentState || conversation.state).bgColor,
                color: getStateColor(currentState || conversation.state).textColor,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontWeight: 'medium'
              }}
            >
              {translateState(currentState || conversation.state)}
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <Box 
        sx={{ 
          flexGrow: 1, 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          pt: 1,
          backgroundColor: theme.palette.background.default
        }}
      >
        {loading ? (
          // Loading skeleton
          Array.from(new Array(5)).map((_, index) => (
            <Box key={index} sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: index % 2 === 0 ? 'flex-start' : 'flex-end' }}>
              <Skeleton variant="rounded" width={isSmall ? '80%' : '60%'} height={60} />
              <Skeleton variant="text" width={100} height={20} sx={{ mt: 1 }} />
            </Box>
          ))
        ) : messages.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
            <Typography variant="body1" color="text.secondary">
              Inga meddelanden än
            </Typography>
            {canSendManualResponse && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Starta konversationen genom att skicka ett meddelande
              </Typography>
            )}
          </Box>
        ) : (
          Object.entries(messageGroups).map(([dateKey, groupMessages]) => {
            const dateLabel = (() => {
              const date = parseISO(dateKey)
              if (isToday(date)) return 'Idag'
              if (isYesterday(date)) return 'Igår'
              return format(date, 'PPPP', { locale: sv })
            })()
            
            return (
              <Box key={dateKey} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      px: 2, 
                      py: 0.5, 
                      borderRadius: 10,
                      color: theme.palette.text.secondary,
                      boxShadow: theme.shadows[1]
                    }}
                  >
                    {dateLabel}
                  </Typography>
                </Box>
                {groupMessages.map(message => (
                  <Message 
                    key={message.id} 
                    message={message} 
                    formatDate={formatDate}
                    isSmall={isSmall}
                  />
                ))}
              </Box>
            )
          })
        )}
        <div ref={messagesEndRef} />
      </Box>
      
      {sendError && (
        <Alert 
          severity="error" 
          sx={{ mx: 2, mt: 1 }}
          onClose={() => setSendError(null)}
        >
          {sendError}
        </Alert>
      )}
      
      {canSendManualResponse && (
        <Box 
          sx={{ 
            p: 2, 
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Skriv ditt svar..."
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={handleKeyPress}
            multiline
            maxRows={4}
            disabled={sendingMessage}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton 
                    onClick={sendManualResponse}
                    disabled={!messageText.trim() || sendingMessage}
                    color="primary"
                    edge="end"
                  >
                    {sendingMessage ? <CircularProgress size={24} /> : <SendIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
              }
            }}
          />
        </Box>
      )}
    </Paper>
  )
}

function Message({ message, formatDate, isSmall }) {
  const theme = useTheme()
  const isInbound = message.direction === 'inbound'
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: isInbound ? 'flex-start' : 'flex-end',
        mb: 1.5
      }}
    >
      <Box sx={{ maxWidth: isSmall ? '80%' : '60%' }}>
        {isInbound && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, ml: 1 }}>
            <PersonOutlineIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
            <Typography variant="caption" color="text.secondary">
              Kund
            </Typography>
          </Box>
        )}
        
        <Card
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: isInbound 
              ? theme.palette.background.paper
              : theme.palette.primary.main,
            color: isInbound 
              ? theme.palette.text.primary
              : theme.palette.primary.contrastText,
            border: isInbound ? `1px solid ${theme.palette.divider}` : 'none',
            wordBreak: 'break-word'
          }}
        >
          <Typography variant="body1">
            {message.content}
          </Typography>
        </Card>
        
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            display: 'block', 
            mt: 0.5,
            textAlign: isInbound ? 'left' : 'right',
            px: 1
          }}
        >
          {formatDate(message.created_at)}
        </Typography>
      </Box>
    </Box>
  )
}

function translateState(state) {
  switch (state) {
    case 'awaiting_initial_feedback':
      return 'Väntar på feedback'
    case 'awaiting_review_response':
      return 'Väntar på recensionssvar'
    case 'manual_conversation':
      return 'Manuell konversation'
    case 'completed':
      return 'Avslutad'
    default:
      return state
  }
}

function getStateColor(state) {
  switch (state) {
    case 'awaiting_initial_feedback':
      return { bgColor: '#E8F3FF', textColor: '#2563EB' } // Info blue
    case 'awaiting_review_response':
      return { bgColor: '#FFF7E6', textColor: '#D97706' } // Warning amber
    case 'manual_conversation':
      return { bgColor: '#ECFDF5', textColor: '#059669' } // Success green
    case 'completed':
      return { bgColor: '#EFF6FF', textColor: '#3730A3' } // Primary indigo
    default:
      return { bgColor: '#F3F4F6', textColor: '#4B5563' } // Grey
  }
} 